// pickupService.js
// Pick Up Draft — Phase A (read-only proof).
//
// This file is the web mirror of the READ half of FirebaseRepository_PickUp.swift.
// It loads a pickUpGames document, decodes it into the exact same shape the iOS app
// works with, and exposes the same turn/round helper functions (ported verbatim from
// the computed properties on PickUpGame in PickUpDraftModels.swift).
//
// PHASE A is reads + decoders + pure helpers ONLY. Listeners, markViewed, and the four
// callable wrappers come in later phases (see the markers at the bottom of this file) —
// they are intentionally omitted now so Phase A can prove the decode contract in isolation.
//
// DECODE GOTCHAS (see plan §2):
//   • `rounds` and `playerPools` are stored as JSON strings (Firestore forbids nested
//     arrays), so both MUST be JSON.parse'd. iOS keeps a direct-value fallback for any
//     pre-migration docs; we mirror that fallback here.
//   • Player pools are keyed by "{position}_{slotInRound}" — the doc's `rounds` is the
//     single source of truth for positions. Never reuse the regular Draft's positions.
//   • Timestamps come back as Firestore Timestamp objects → convert with .toDate().

import {
  collection,
  doc,
  getDoc,
  updateDoc,
  onSnapshot,
  arrayUnion,
  query,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";

// ─── Sport mapping ────────────────────────────────────────────────────────────
// The doc stores the raw capitalized enum ("Football"). The web works in lowercase
// ids ("football"), so we convert on decode and keep both for convenience.
const ID_FROM_RAW = {
  Football: "football",
  Baseball: "baseball",
  Basketball: "basketball",
  Hockey: "hockey",
  Soccer: "soccer",
};

function sportIdFromRaw(raw) {
  return ID_FROM_RAW[raw] || null;
}

const RAW_FROM_ID = Object.fromEntries(
  Object.entries(ID_FROM_RAW).map(([raw, id]) => [id, raw])
);

function sportRawFromId(id) {
  return RAW_FROM_ID[id] || null;
}

function toDate(ts) {
  return ts && typeof ts.toDate === "function" ? ts.toDate() : null;
}

// ─── Field decoders (mirror FirebaseRepository_PickUp.swift) ────────────────────

// Player: name, city, jerseyNumber, year, overallRating, position (all required).
function decodePlayer(data) {
  if (!data) return null;
  const { name, city, jerseyNumber, year, overallRating, position } = data;
  if (
    typeof name !== "string" ||
    typeof city !== "string" ||
    typeof jerseyNumber !== "number" ||
    typeof year !== "number" ||
    typeof overallRating !== "number" ||
    typeof position !== "string"
  ) {
    return null;
  }
  return { name, city, jerseyNumber, year, overallRating, position };
}

function decodePick(data) {
  if (!data) return null;
  const player = decodePlayer(data.player);
  if (
    typeof data.userID !== "string" ||
    typeof data.position !== "string" ||
    !player ||
    typeof data.pickIndex !== "number" ||
    typeof data.roundIndex !== "number" ||
    typeof data.slotInRound !== "number" ||
    typeof data.isAutoPick !== "boolean"
  ) {
    return null;
  }
  return {
    id: `${data.userID}_${data.pickIndex}`,
    userID: data.userID,
    position: data.position,
    player,
    pickIndex: data.pickIndex,
    roundIndex: data.roundIndex,
    slotInRound: data.slotInRound,
    timestamp: toDate(data.timestamp),
    isAutoPick: data.isAutoPick,
  };
}

// tiebreaker ∈ "chemistry" | "draft_order" | "forfeit" | null (see plan §2d / §7).
function decodeResult(data) {
  if (!data) return null;
  if (
    typeof data.winnerID !== "string" ||
    typeof data.winnerRating !== "number" ||
    typeof data.winnerChemistry !== "number" ||
    typeof data.loserID !== "string" ||
    typeof data.loserRating !== "number" ||
    typeof data.loserChemistry !== "number"
  ) {
    return null;
  }
  return {
    winnerID: data.winnerID,
    winnerRating: data.winnerRating,
    winnerChemistry: data.winnerChemistry,
    loserID: data.loserID,
    loserRating: data.loserRating,
    loserChemistry: data.loserChemistry,
    tiebreaker: typeof data.tiebreaker === "string" ? data.tiebreaker : null,
    resolvedAt: toDate(data.resolvedAt),
  };
}

// playerPools: "roundIndex" → "position_slotInRound" → [Player]
function decodePlayerPools(raw) {
  const pools = {};
  if (!raw || typeof raw !== "object") return pools;
  for (const [roundKey, roundValue] of Object.entries(raw)) {
    if (!roundValue || typeof roundValue !== "object") continue;
    const decodedRound = {};
    for (const [poolKey, poolValue] of Object.entries(roundValue)) {
      if (!Array.isArray(poolValue)) continue;
      decodedRound[poolKey] = poolValue
        .map(decodePlayer)
        .filter((p) => p !== null);
    }
    pools[roundKey] = decodedRound;
  }
  return pools;
}

// rounds is a JSON string of [[position]]; fall back to a direct array if present.
function parseRounds(rawRounds) {
  if (typeof rawRounds === "string") {
    try {
      const parsed = JSON.parse(rawRounds);
      if (Array.isArray(parsed)) return parsed;
    } catch (_) {
      /* fall through to fallback / null */
    }
  }
  if (Array.isArray(rawRounds)) return rawRounds; // pre-migration direct value
  return null;
}

// playerPools is a JSON string; fall back to a direct map if present.
function parsePlayerPools(rawPools) {
  if (typeof rawPools === "string") {
    try {
      const parsed = JSON.parse(rawPools);
      if (parsed && typeof parsed === "object") return decodePlayerPools(parsed);
    } catch (_) {
      /* fall through */
    }
  }
  if (rawPools && typeof rawPools === "object") return decodePlayerPools(rawPools);
  return null;
}

/**
 * Decodes a pickUpGames document into the canonical game object. Returns null (and logs
 * which field failed in dev) if any required field is missing or malformed — same
 * fail-closed behavior as the iOS decoder.
 */
export function decodePickUpGame(snap) {
  if (!snap || !snap.exists()) return null;
  const d = snap.data() || {};

  const sport = sportIdFromRaw(d.sport);
  const rounds = parseRounds(d.rounds);
  const playerPools = parsePlayerPools(d.playerPools);

  const required =
    typeof d.id === "string" &&
    typeof d.playerAID === "string" &&
    typeof d.playerBID === "string" &&
    typeof d.playerAUsername === "string" &&
    typeof d.playerBUsername === "string" &&
    sport !== null &&
    typeof d.status === "string" &&
    Array.isArray(d.turnOrder) &&
    typeof d.currentPickIndex === "number" &&
    typeof d.currentRoundIndex === "number" &&
    Array.isArray(d.picks) &&
    d.missedPickCounts && typeof d.missedPickCounts === "object" &&
    typeof d.firstPickerID === "string" &&
    Array.isArray(d.viewedBy) &&
    rounds !== null &&
    playerPools !== null;

  if (!required) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn(`pickupService: failed to decode PickUpGame from ${snap.id}`, {
        sportOk: sport !== null,
        roundsOk: rounds !== null,
        poolsOk: playerPools !== null,
        raw: d,
      });
    }
    return null;
  }

  return {
    id: d.id,
    playerAID: d.playerAID,
    playerBID: d.playerBID,
    playerAUsername: d.playerAUsername,
    playerBUsername: d.playerBUsername,
    sport,                 // lowercase id, e.g. "football"
    sportRaw: d.sport,     // raw enum, e.g. "Football"
    status: d.status,      // "waiting" | "active" | "complete" | "forfeited"
    turnOrder: d.turnOrder,
    currentPickIndex: d.currentPickIndex,
    currentRoundIndex: d.currentRoundIndex,
    rounds,
    playerPools,
    picks: d.picks.map(decodePick).filter((p) => p !== null),
    missedPickCounts: d.missedPickCounts,
    firstPickerID: d.firstPickerID,
    createdAt: toDate(d.createdAt),
    lastActionAt: toDate(d.lastActionAt),
    turnExpiresAt: toDate(d.turnExpiresAt),
    result: decodeResult(d.result),
    viewedBy: d.viewedBy,
  };
}

/** One-time read of a single game by ID. */
export async function loadPickUpGame(gameID) {
  const snap = await getDoc(doc(db, "pickUpGames", gameID));
  return decodePickUpGame(snap);
}

/**
 * Reads the user's own W/L versus records: users/{uid}/appData/versusRecords →
 * { "Football": { wins, losses }, ... }. Returns a map keyed by lowercase sport id with
 * every sport present (zeroed when absent). Owner-only read.
 */
export async function loadVersusRecords(userID) {
  const out = {};
  for (const id of Object.values(ID_FROM_RAW)) out[id] = { wins: 0, losses: 0 };
  try {
    const snap = await getDoc(doc(db, "users", userID, "appData", "versusRecords"));
    if (!snap.exists()) return out;
    const d = snap.data() || {};
    for (const [raw, id] of Object.entries(ID_FROM_RAW)) {
      const s = d[raw];
      if (s) out[id] = { wins: s.wins || 0, losses: s.losses || 0 };
    }
  } catch (_) {
    /* fall back to zeros */
  }
  return out;
}

// ─── Turn / round helpers (ported from PickUpGame computed props) ───────────────
// Pure functions over a decoded game object so they're trivially testable and shared
// by every screen (no duplicate logic across draft / hub / complete).

export function currentTurn(game) {
  if (game.currentPickIndex >= game.turnOrder.length) return game.playerAID;
  return game.turnOrder[game.currentPickIndex];
}

export function isUserTurn(game, userID) {
  return game.status === "active" && currentTurn(game) === userID;
}

export function totalPicks(game) {
  return game.turnOrder.length;
}

export function isAllPicksComplete(game) {
  return game.picks.length >= totalPicks(game);
}

export function opponentID(game, userID) {
  return userID === game.playerAID ? game.playerBID : game.playerAID;
}

export function opponentUsername(game, userID) {
  return userID === game.playerAID ? game.playerBUsername : game.playerAUsername;
}

export function picksFor(game, userID) {
  return game.picks.filter((p) => p.userID === userID);
}

export function draftedPlayers(game, userID) {
  return picksFor(game, userID)
    .slice()
    .sort((a, b) => a.pickIndex - b.pickIndex)
    .map((p) => p.player);
}

export function draftedPositions(game, userID) {
  return picksFor(game, userID)
    .slice()
    .sort((a, b) => a.pickIndex - b.pickIndex)
    .map((p) => p.position);
}

export function currentRoundPositions(game) {
  if (game.currentRoundIndex >= game.rounds.length) return [];
  return game.rounds[game.currentRoundIndex];
}

/** The 5-player pool for a specific round slot (compound key "{position}_{slot}"). */
export function pool(game, roundIndex, position, slotInRound) {
  const round = game.playerPools[String(roundIndex)];
  if (!round) return [];
  return round[`${position}_${slotInRound}`] || [];
}

/** Names already picked from pools in a round, by either player. */
export function takenPlayerNames(game, roundIndex) {
  return new Set(
    game.picks
      .filter((p) => p.roundIndex === roundIndex)
      .map((p) => p.player.name)
  );
}

/** (position, slotInRound) pairs the user has NOT yet filled in a round. */
export function unfilledSlots(game, userID, roundIndex) {
  if (roundIndex >= game.rounds.length) return [];
  const positions = game.rounds[roundIndex];
  const filledSlots = new Set(
    game.picks
      .filter((p) => p.userID === userID && p.roundIndex === roundIndex)
      .map((p) => p.slotInRound)
  );
  const unfilled = [];
  positions.forEach((position, slotIndex) => {
    if (!filledSlots.has(slotIndex)) unfilled.push({ position, slotInRound: slotIndex });
  });
  return unfilled;
}

export function hasUserFilledRound(game, userID, roundIndex) {
  return unfilledSlots(game, userID, roundIndex).length === 0;
}

export function isRoundComplete(game, roundIndex) {
  return (
    hasUserFilledRound(game, game.playerAID, roundIndex) &&
    hasUserFilledRound(game, game.playerBID, roundIndex)
  );
}

export function isSlotFilled(game, userID, roundIndex, slotInRound) {
  return game.picks.some(
    (p) =>
      p.userID === userID &&
      p.roundIndex === roundIndex &&
      p.slotInRound === slotInRound
  );
}

/**
 * Tiebreaker note for the complete screen — matches iOS PickUpResult.tiebreakerDescription
 * exactly: only "chemistry" and "draft_order" produce a note; "forfeit" and null show
 * nothing. (See plan §7.)
 */
export function tiebreakerNote(tiebreaker) {
  switch (tiebreaker) {
    case "chemistry":
      return "Won by chemistry bonus";
    case "draft_order":
      return "Won by draft order";
    default:
      return null;
  }
}

// ─── Live game listener + markViewed (Phase C) ────────────────────────────────

/**
 * Listens to a single pickUpGames doc. Calls onChange(decodedGame | null) on every update.
 * Returns the onSnapshot unsubscribe — detach on unmount (plan §8).
 */
export function listenToPickUpGame(gameID, onChange) {
  return onSnapshot(
    doc(db, "pickUpGames", gameID),
    (snap) => onChange(decodePickUpGame(snap)),
    (err) => {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("pickupService: game listener error", err);
      }
      onChange(null);
    }
  );
}

/**
 * Marks a completed game as viewed by adding the user's UID to viewedBy. This is the ONLY
 * client write the rules permit on pickUpGames (affectedKeys hasOnly ['viewedBy']).
 */
export async function markPickUpGameViewed(gameID, userID) {
  await updateDoc(doc(db, "pickUpGames", gameID), {
    viewedBy: arrayUnion(userID),
  });
}

// ─── Friend challenges: decode + listeners + decline/cancel (Phase C) ──────────
// Challenges are created/accepted via callables (below); decline/cancel are direct doc
// updates the rules allow (recipient → declined, sender → cancelled).

function decodeFriendChallenge(snap) {
  const d = snap.data();
  if (!d) return null;
  const sport = sportIdFromRaw(d.sport);
  if (
    typeof d.fromUID !== "string" ||
    typeof d.fromUsername !== "string" ||
    typeof d.toUID !== "string" ||
    typeof d.toUsername !== "string" ||
    sport === null ||
    typeof d.status !== "string"
  ) {
    return null;
  }
  return {
    id: snap.id,
    fromUID: d.fromUID,
    fromUsername: d.fromUsername,
    toUID: d.toUID,
    toUsername: d.toUsername,
    sport,        // lowercase id
    sportRaw: d.sport,
    status: d.status, // "pending"|"accepted"|"declined"|"cancelled"|"expired"
    createdAt: toDate(d.createdAt),
    expiresAt: toDate(d.expiresAt),
    acceptedGameID: typeof d.acceptedGameID === "string" ? d.acceptedGameID : null,
  };
}

// A pending challenge whose expiresAt has passed but hasn't yet been swept to "expired"
// by the scheduled Cloud Function should not be shown. Mirrors iOS `!isExpired`.
function challengeNotExpired(c) {
  return !c.expiresAt || c.expiresAt.getTime() > Date.now();
}

// friend_challenges where toUID == me & status == pending
export function listenToIncomingChallenges(userID, onChange) {
  return onSnapshot(
    query(
      collection(db, "friend_challenges"),
      where("toUID", "==", userID),
      where("status", "==", "pending")
    ),
    (snap) =>
      onChange(snap.docs.map(decodeFriendChallenge).filter((c) => c !== null && challengeNotExpired(c))),
    (err) => {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("pickupService: incoming challenges listener error", err);
      }
    }
  );
}

// friend_challenges where fromUID == me & status in [pending, accepted]
// (accepted is included so the sender's listener can pick up acceptedGameID and route in)
export function listenToSentChallenges(userID, onChange) {
  return onSnapshot(
    query(
      collection(db, "friend_challenges"),
      where("fromUID", "==", userID),
      where("status", "in", ["pending", "accepted"])
    ),
    // The query includes "accepted" so a future transition could be detected, but only
    // pending, non-expired challenges are shown — accepted ones have become live games
    // and surface in ACTIVE DRAFTS instead. Mirrors FriendChallengeManager.
    (snap) =>
      onChange(
        snap.docs
          .map(decodeFriendChallenge)
          .filter((c) => c !== null && c.status === "pending" && challengeNotExpired(c))
      ),
    (err) => {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("pickupService: sent challenges listener error", err);
      }
    }
  );
}

export async function declineFriendChallenge(challengeID) {
  await updateDoc(doc(db, "friend_challenges", challengeID), { status: "declined" });
}

export async function cancelFriendChallenge(challengeID) {
  await updateDoc(doc(db, "friend_challenges", challengeID), { status: "cancelled" });
}

// ─── The four callables (Phase C) ──────────────────────────────────────────────
// httpsCallable attaches the auth token automatically. Each returns the function's
// response payload (res.data). Errors surface as FirebaseError with a `.code` — map them
// for display with callableErrorMessage below.

export async function processPickUpTurn({ gameID, roundIndex, position, slotInRound, selectedPlayerName }) {
  const fn = httpsCallable(functions, "processPickUpTurn");
  const res = await fn({ gameID, roundIndex, position, slotInRound, selectedPlayerName });
  return res.data; // { isComplete, currentPickIndex, currentRoundIndex, status }
}

export async function forfeitPickUpGame(gameID) {
  const fn = httpsCallable(functions, "forfeitPickUpGame");
  const res = await fn({ gameID });
  return res.data; // { success: true }
}

// sport: pass the lowercase id; converted to the raw enum the function expects.
export async function sendFriendChallenge({ toUID, toUsername, sportId }) {
  const raw = sportRawFromId(sportId);
  if (!raw) throw new Error(`Unknown sport: ${sportId}`);
  const fn = httpsCallable(functions, "sendFriendChallenge");
  const res = await fn({ toUID, toUsername, sport: raw });
  return res.data; // { success: true, challengeID }
}

export async function acceptFriendChallenge(challengeID) {
  const fn = httpsCallable(functions, "acceptFriendChallenge");
  const res = await fn({ challengeID });
  return res.data; // { success: true, gameID }
}

/**
 * Maps a callable FirebaseError to a user-facing message. Covers the codes the four
 * functions actually throw (see plan §3). Unknown codes fall back to the raw message.
 */
export function callableErrorMessage(error) {
  switch (error?.code) {
    case "unauthenticated":
      return "You need to be signed in.";
    case "failed-precondition":
      return "That action isn't available right now (not your turn, or already resolved).";
    case "already-exists":
      return "That's already been done (slot taken, or a challenge is already pending).";
    case "not-found":
      return "Not found — it may have been removed.";
    case "permission-denied":
      return "You're not allowed to do that.";
    case "deadline-exceeded":
      return "This challenge has expired.";
    case "resource-exhausted":
      return "You've hit a limit (too many active games or outstanding challenges).";
    case "invalid-argument":
      return "Something was missing from that request.";
    default:
      return error?.message || "Something went wrong.";
  }
}
